/* eslint-disable no-process-env, no-console */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon from '@ux/icon';
import ChevronIcon from '@ux/icon/chevron-down';
import LinkArrowIcon from '@ux/icon/link-arrow';
import WindowNewIcon from '@ux/icon/window-new';

const UX_BUTTON_CLASSNAME = 'ux-button';

//
// Valid HTML Button types.
//
const buttonTypes = ['button', 'reset', 'submit'];

const designs = [
  'critical',
  'inline',
  'primary',
  'secondary',
  'control',
  'stateless'
];

/**
* Display a button from a set of predefined types.
*
* @displayName Button
* @class Button
* @public
*/
export class Button extends Component {
  constructor(props) {
    super(props);
    this._warn();
  }

  get _ariaAttributes() {
    return {
      'aria-label': this.props['aria-label'],
      'aria-labelledby': this.props['aria-labelledby'],
      'aria-haspopup': this.props['aria-haspopup'],
      'aria-disabled': this.props['aria-disabled'],
      'aria-controls': this.props['aria-controls']
    };
  }

  _warn() {
    const {
      children,
      icon,
      text
    } = this.props;

    if (process.env.NODE_ENV === 'production') return;

    if (children) {
      console.warn(`Warning: The use of children elements within @ux/button is deprecated.
        Please use the 'text' prop instead.`);
    }

    const missingText = !(text || children);
    const missingLabel = !(this._ariaAttributes['aria-label'] || this._ariaAttributes['aria-labelledby']);
    if (icon && missingText && missingLabel) {
      console.warn(`Warning: Buttons may not be accessible containing only an icon,
        consider adding the 'aria-label' or 'aria-labelledby' prop to the @ux/button.`);
    }
  }

  /**
   * Render the button element with predefined classes and restricted designs.
   *
   * @returns {ReactElement} Button element.
   * @private
   */
  render() { // eslint-disable-line complexity, max-statements
    const {
      active,
      as,
      className,
      children,
      design,
      disabled,
      download,
      external,
      href,
      icon,
      id,
      onBlur,
      onClick,
      onFocus,
      onKeyDown,
      onKeyPress,
      onKeyUp,
      onMouseDown,
      onMouseEnter,
      onTouchStart,
      size,
      text,
      title,
      type
    } = this.props;

    const TagName = href ? 'a' : 'button';
    const classes = [UX_BUTTON_CLASSNAME];

    // Only allow data-* attributes to pass-through
    const passThroughProps = Object.keys(this.props).reduce((curr, propName) => {
      if (propName.startsWith('data-')) {
        curr[propName] = this.props[propName];
      }
      return curr;
    }, {});

    let accessory;
    let ariaActiveAttr;
    let buttonType;
    let role;

    const content = (children && !text) ? children : text;

    if (!href) {
      buttonType = buttonTypes.includes(type.toLowerCase()) ? type : buttonTypes[0];

      const prepareIcon = {
        'aria-hidden': true,
        'className': `${UX_BUTTON_CLASSNAME}-accessory`
      };

      switch (as) {
        case 'select':
          accessory = <ChevronIcon { ...prepareIcon }/>;
          ariaActiveAttr = 'aria-expanded';
          break;
        case 'cta':
          accessory = <LinkArrowIcon { ...prepareIcon }/>;
          break;
        case 'external':
          accessory = <WindowNewIcon { ...prepareIcon }/>;
          break;
        case 'toggle':
          ariaActiveAttr = 'aria-checked';
          role = 'switch';
          break;
        default:
          ariaActiveAttr = 'aria-pressed';
      }
    }

    if (designs.includes(design)) {
      classes.push(`${UX_BUTTON_CLASSNAME}-${design}`);
    }

    if (size === 'small') {
      classes.push(`${UX_BUTTON_CLASSNAME}-small`);
    }

    if (!content) {
      classes.push(`${UX_BUTTON_CLASSNAME}-square`);
    }

    let iconComponent;
    if (icon) {
      const { className: clsName } = icon.props || {};
      const iconClassName = classNames(`${UX_BUTTON_CLASSNAME}-icon`, clsName);
      iconComponent = React.cloneElement(icon, { className: iconClassName });
    }

    return <TagName
      { ...this._ariaAttributes }
      { ...(typeof active === 'boolean' && { [ariaActiveAttr]: active }) }
      className={ classNames(classes.join(' '), className) }
      disabled={ disabled }
      download={ href && download }
      href={ href }
      id={ id }
      { ...({ onBlur, onClick, onFocus, onKeyDown, onKeyPress, onKeyUp, onMouseEnter, onTouchStart, onMouseDown }) }
      role={ role }
      target={ href && external ? '_blank' : null }
      title={ title }
      type={ buttonType }
      { ...passThroughProps }>
      { iconComponent }
      { content && <span className={ `${UX_BUTTON_CLASSNAME}-text` }>{ content }</span> }
      { accessory }
    </TagName>;
  }
}

Button.defaultProps = {
  type: 'button'
};

Button.designs = designs;

Button.propTypes = {
  /** DEPRECATED prop to render children elements */
  'children': PropTypes.node,
  /** Boolean for a11y support */
  'active': PropTypes.bool,
  /** aria-controls prop for a11y */
  'aria-controls': PropTypes.string,
  /** Label for a11y support */
  'aria-label': PropTypes.string,
  /** Labeledby for a11y support */
  'aria-labelledby': PropTypes.string,
  /** haspopup for a11y support */
  'aria-haspopup': PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool
  ]),
  /** disabled for a11y support */
  'aria-disabled': PropTypes.bool,
  /** Displays the button with different properties and a11y support */
  'as': PropTypes.oneOf(['cta', 'select', 'toggle', 'external']),
  /** Classes applied to a button for positioning purposes */
  'className': PropTypes.string,
  /** Visual style of the component */
  'design': PropTypes.oneOf(Button.designs),
  /** Sets the disabled state of a button */
  'disabled': PropTypes.bool,
  /** Allows for link to be downloadable and take optional download file name */
  'download': PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string
  ]),
  /** Link opens in a new tab/page */
  'external': PropTypes.bool,
  /** Uri for where a button links to, which turns a button into an <a> tag */
  'href': PropTypes.string,
  /** Optional icon from @ux/icon for the button, appears on the left */
  'icon': PropTypes.shape({ type: Icon.type, props: PropTypes.object }),
  /** Id applied to a button */
  'id': PropTypes.string,
  /** Blur handler for a button */
  'onBlur': PropTypes.func,
  /** Click handler for a button */
  'onClick': PropTypes.func,
  /** Focus handler for a button */
  'onFocus': PropTypes.func,
  /** Key Down handler for a button */
  'onKeyDown': PropTypes.func,
  /** Key Press handler for a button */
  'onKeyPress': PropTypes.func,
  /** Key Up handler for a button */
  'onKeyUp': PropTypes.func,
  /** Mouse Down handler for a button */
  'onMouseDown': PropTypes.func,
  /** Mouse Enter handler for a button */
  'onMouseEnter': PropTypes.func,
  /** Touch Start handler for a button */
  'onTouchStart': PropTypes.func,
  /** Size of the button */
  'size': PropTypes.oneOf(['small']),
  /** Contents of the button */
  'text': PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  /** An optional title-attribute for the button element, frequently displayed as a tooltip by browsers */
  'title': PropTypes.string,
  /** Button type */
  'type': PropTypes.string
};

export default Button;
